<div class="tln-page" [formGroup]="form">
  <header class="tln-bar">
    <div class="tln-bar__inner">
      <div class="tln-bar__left">
        <button
          type="button"
          class="tln-icon-btn tln-bar__back"
          data-testid="tln-back"
          [attr.aria-label]="'testRunNew.back' | translate"
          [attr.title]="'testRunNew.back' | translate"
          (click)="goToOverview()"
        >
          <mat-icon aria-hidden="true">chevron_left</mat-icon>
        </button>
        <div>
          <p class="tln-bar__eyebrow" data-testid="tln-eyebrow">
            @if (isEdit) {
              {{ 'testRunNew.edit.eyebrow' | translate: { key: settings()?.key ?? '' } }}
            } @else {
              {{ 'testRunNew.eyebrow.' + art() | translate }}
            }
          </p>
          <h1 class="tln-bar__title" data-testid="tln-title">
            {{ (isEdit ? 'testRunNew.edit.title' : 'testRunNew.title') | translate }}
          </h1>
        </div>
        @if (dirty()) {
          <span class="tln-chip" data-testid="tln-dirty">
            <span class="tln-chip__dot" aria-hidden="true"></span>
            {{ 'testRunNew.dirty' | translate }}
          </span>
        }
      </div>
      <div class="tln-bar__actions">
        <button type="button" class="tln-btn tln-btn--ghost" data-testid="tln-cancel" (click)="goToOverview()">
          {{ 'testRunNew.cancel' | translate }}
        </button>
        <button
          type="button"
          class="tln-btn tln-btn--primary"
          data-testid="tln-submit"
          [disabled]="!canSubmit()"
          [attr.title]="submitHintKey() ? (submitHintKey()! | translate) : null"
          (click)="submit()"
        >
          <mat-icon aria-hidden="true">check</mat-icon>
          {{ (isEdit ? 'testRunNew.edit.submit' : submitLabelKey()) | translate }}
        </button>
      </div>
    </div>
  </header>

  <app-project-nav></app-project-nav>

  @if (loadError()) {
    <p class="tln-error" role="alert" data-testid="tln-load-error">{{ 'testRunNew.loadError' | translate }}</p>
  }
  @if (runLoadError()) {
    <p class="tln-error" role="alert" data-testid="tln-run-load-error">{{ 'testRunNew.edit.loadError' | translate }}</p>
  }
  @if (saveError(); as message) {
    <p class="tln-error" role="alert" data-testid="tln-save-error">{{ message }}</p>
  }

  <div class="tln-grid">
    <div class="tln-main">
      <!-- Quelle -->
      <section class="tln-card" aria-labelledby="tln-source-heading">
        <to-section-title
          headingId="tln-source-heading"
          [title]="'testRunNew.source.title' | translate"
          [subtitle]="'testRunNew.source.subtitle.' + art() | translate"
        ></to-section-title>

        @if (isEdit) {
          <p class="tln-hint tln-hint--locked" data-testid="tln-composition-locked">
            <mat-icon aria-hidden="true">lock</mat-icon>
            {{ 'testRunNew.edit.compositionLocked' | translate }}
          </p>
        }

        <div class="tln-tiles" role="group" [attr.aria-label]="'testRunNew.source.title' | translate">
          @for (tile of sourceTiles(); track tile) {
            <button
              type="button"
              class="tln-tile"
              data-testid="tln-source-tile"
              [attr.data-source]="tile"
              [class.tln-tile--on]="activeSourceTile() === tile"
              [attr.aria-pressed]="activeSourceTile() === tile"
              [disabled]="isEdit"
              (click)="setQuelle(tile)"
            >
              <span class="tln-radio" aria-hidden="true"></span>
              <span class="tln-tile__label">{{ 'testRunNew.source.' + tile | translate }}</span>
            </button>
          }
        </div>

        @switch (quelle()) {
          @case ('REPO') {
            <p class="tln-hint tln-hint--accent" data-testid="tln-repo-hint">
              {{ 'testRunNew.source.repoHint' | translate }}
            </p>
          }
          @case ('PLAN') {
            @if (plans().length === 0) {
              <p class="tln-empty" data-testid="tln-plan-empty">{{ 'testRunNew.source.planEmpty' | translate }}</p>
            }
            <ul class="tln-list tln-list--plan">
              @for (plan of plans(); track plan.id) {
                <li>
                  <button
                    type="button"
                    class="tln-row tln-row--plan"
                    data-testid="tln-plan-row"
                    [attr.data-plan-id]="plan.id"
                    [class.tln-row--on]="isPlanSelected(plan.id)"
                    [attr.aria-pressed]="isPlanSelected(plan.id)"
                    [disabled]="isEdit"
                    (click)="togglePlan(plan.id)"
                  >
                    <span class="tln-check" aria-hidden="true">
                      @if (isPlanSelected(plan.id)) {
                        <mat-icon>check</mat-icon>
                      }
                    </span>
                    <span class="tln-tile-icon" data-testid="tln-plan-icon" aria-hidden="true">
                      <mat-icon>shield</mat-icon>
                    </span>
                    <code class="tln-key">{{ plan.publicId ?? plan.id }}</code>
                    <span class="tln-row__label">{{ plan.title }}</span>
                    <span class="tln-row__meta">
                      {{ 'testRunNew.source.planCaseCount' | translate: { count: plan.testCaseIds.length } }}
                    </span>
                  </button>
                </li>
              }
            </ul>
            <p class="tln-hint" data-testid="tln-plan-hint">
              {{ 'testRunNew.source.planHint' | translate: { count: selectedPlanCount() } }}
            </p>
          }
          @default {
            <div class="tln-search">
              <mat-icon aria-hidden="true">search</mat-icon>
              <input
                type="search"
                data-testid="tln-case-search"
                [attr.aria-label]="'testRunNew.source.searchLabel' | translate"
                [placeholder]="'testRunNew.source.searchPlaceholder' | translate"
                [value]="search()"
                [attr.maxlength]="fieldLengths.NAME_MAX"
                [disabled]="isEdit"
                (input)="onSearchInput($any($event.target).value)"
              />
            </div>
            @if (cases().length === 0) {
              <p class="tln-empty" data-testid="tln-case-empty">{{ 'testRunNew.source.caseEmpty' | translate }}</p>
            }
            <ul class="tln-list">
              @for (testCase of cases(); track testCase.id) {
                <li>
                  <button
                    type="button"
                    class="tln-row"
                    data-testid="tln-case-row"
                    [attr.data-case-id]="testCase.id"
                    [class.tln-row--on]="isCaseSelected(testCase.id)"
                    [attr.aria-pressed]="isCaseSelected(testCase.id)"
                    [disabled]="isEdit"
                    (click)="toggleCase(testCase.id)"
                  >
                    <span class="tln-check" aria-hidden="true">
                      @if (isCaseSelected(testCase.id)) {
                        <mat-icon>check</mat-icon>
                      }
                    </span>
                    <code class="tln-key">{{ testCase.key }}</code>
                    <span class="tln-row__label">{{ testCase.title }}</span>
                  </button>
                </li>
              }
            </ul>
          }
        }
      </section>

      @if (art() === 'AUTOMATED' && quelle() === 'REPO') {
        <app-repo-anbindung
          data-testid="tln-repo-card"
          [repositories]="repositories()"
          [pipelines]="pipelines()"
          [selectedRepositoryId]="selectedRepositoryId()"
          [selectedPipelineFile]="selectedPipelineFile()"
          [readBranch]="pipelinesBranch()"
          [pipelinesLoading]="pipelinesLoading()"
          [pipelinesErrorCode]="pipelinesErrorCode()"
          [repositoriesError]="repositoriesError()"
          [disabled]="isEdit"
          (repositorySelected)="onRepositorySelected($event)"
          (pipelineSelected)="onPipelineSelected($event)"
        ></app-repo-anbindung>
      }

      <!-- Auswahl-Übersicht -->
      @if (quelle() !== 'REPO') {
        <section class="tln-card" data-testid="tln-selection-card" aria-labelledby="tln-selection-heading">
          <to-section-title
            headingId="tln-selection-heading"
            [title]="'testRunNew.selection.title' | translate"
            [subtitle]="'testRunNew.selection.subtitle' | translate"
          ></to-section-title>

          <p class="tln-counters">
            <span>{{ 'testRunNew.selection.planCount' | translate: { count: selectedPlanCount() } }}</span>
            <span>{{ 'testRunNew.selection.caseCount' | translate: { count: selectedCaseCount() } }}</span>
            <strong>{{ 'testRunNew.selection.totalCount' | translate: { count: totalCaseCount() } }}</strong>
          </p>

          @if (selection().length === 0) {
            <p class="tln-empty" data-testid="tln-selection-empty">{{ 'testRunNew.selection.empty' | translate }}</p>
          }

          <!-- table_chart statt `image` aus page-testausfuehrung.jsx:584: das uebrige
               Bundle und die Projekt-Navigation kennzeichnen Testdatenblaetter mit
               dem Tabellen-Symbol. -->
          @if (wholeRunSheets().length > 0) {
            <p class="tln-allrun" data-testid="tln-allrun-sheets">
              <mat-icon aria-hidden="true">table_chart</mat-icon>
              <span class="tln-allrun__label">{{ 'testRunNew.selection.wholeRun' | translate }}</span>
              @for (sheet of wholeRunSheets(); track sheet.id) {
                <span class="tln-allrun__sheet">{{ sheet.key }} · {{ sheet.name }}</span>
              }
            </p>
          }

          <ol class="tln-order">
            @for (item of selection(); track item.id; let i = $index, last = $last) {
              <li class="tln-order__item" data-testid="tln-selection-row" [attr.data-item-id]="item.id">
                <div class="tln-order__head">
                  <span class="tln-order__no" aria-hidden="true">{{ i + 1 }}</span>
                  @if (item.kind === 'PLAN') {
                    <button
                      type="button"
                      class="tln-icon-btn tln-icon-btn--compact"
                      data-testid="tln-expand-plan"
                      [attr.aria-expanded]="isExpanded(item.id)"
                      [attr.aria-label]="
                        (isExpanded(item.id) ? 'testRunNew.selection.collapse' : 'testRunNew.selection.expand')
                          | translate: { key: item.key }
                      "
                      (click)="togglePlanExpanded(item.id)"
                    >
                      <mat-icon aria-hidden="true">
                        {{ isExpanded(item.id) ? 'expand_more' : 'chevron_right' }}
                      </mat-icon>
                    </button>
                  }
                  <code class="tln-key">{{ item.key }}</code>
                  <span class="tln-order__label">{{ item.label }}</span>
                  @if (item.caseCount !== null) {
                    <span class="tln-order__meta">
                      {{ 'testRunNew.source.planCaseCount' | translate: { count: item.caseCount } }}
                    </span>
                  }
                  @for (sheet of sheetsForTarget(item.id); track sheet.id) {
                    <span class="tln-sheet-chip" data-testid="tln-sheet-chip">
                      <mat-icon aria-hidden="true">table_chart</mat-icon>
                      {{ sheet.key }}
                    </span>
                  }
                  <span class="tln-order__actions">
                    <button
                      type="button"
                      class="tln-icon-btn"
                      data-testid="tln-move-up"
                      [disabled]="isEdit || i === 0"
                      [attr.aria-label]="'testRunNew.selection.moveUp' | translate: { key: item.key }"
                      (click)="moveSelection(i, -1)"
                    >
                      <mat-icon aria-hidden="true">arrow_upward</mat-icon>
                    </button>
                    <button
                      type="button"
                      class="tln-icon-btn"
                      data-testid="tln-move-down"
                      [disabled]="isEdit || last"
                      [attr.aria-label]="'testRunNew.selection.moveDown' | translate: { key: item.key }"
                      (click)="moveSelection(i, 1)"
                    >
                      <mat-icon aria-hidden="true">arrow_downward</mat-icon>
                    </button>
                    <button
                      type="button"
                      class="tln-icon-btn tln-icon-btn--danger"
                      data-testid="tln-remove"
                      [disabled]="isEdit"
                      [attr.aria-label]="'testRunNew.selection.remove' | translate: { key: item.key }"
                      (click)="removeSelection(item.id)"
                    >
                      <mat-icon aria-hidden="true">close</mat-icon>
                    </button>
                  </span>
                </div>
                @if (item.kind === 'PLAN' && isExpanded(item.id)) {
                  <ol class="tln-order__cases">
                    @for (planCase of planCases(item.id); track planCase.id; let ci = $index) {
                      <li data-testid="tln-plan-case">
                        <span class="tln-order__caseno" aria-hidden="true">{{ ci + 1 }}</span>
                        <code class="tln-key">{{ planCase.key }}</code>
                        <span>{{ planCase.title }}</span>
                      </li>
                    }
                  </ol>
                }
              </li>
            }
          </ol>
        </section>
      }

      <!-- Testdatenblätter -->
      <section class="tln-card" aria-labelledby="tln-sheets-heading">
        <to-section-title
          headingId="tln-sheets-heading"
          [title]="'testRunNew.sheets.title' | translate"
          [subtitle]="'testRunNew.sheets.subtitle' | translate"
        ></to-section-title>

        @if (assignments().length === 0) {
          <p class="tln-empty" data-testid="tln-assignment-empty">{{ 'testRunNew.sheets.empty' | translate }}</p>
        }

        <ul class="tln-list">
          @for (assignment of assignments(); track $index; let i = $index) {
            <li class="tln-assignment" data-testid="tln-assignment-row">
              <span class="tln-tile-icon" data-testid="tln-sheet-icon" aria-hidden="true">
                <mat-icon>table_chart</mat-icon>
              </span>
              <code class="tln-key">{{ sheetById(assignment.id)?.key }}</code>
              <span class="tln-assignment__name">{{ sheetById(assignment.id)?.name }}</span>
              <span class="tln-assignment__to">{{ 'testRunNew.sheets.assignedTo' | translate }}</span>
              <mat-form-field appearance="fill" class="tln-select tln-select--compact">
                <mat-label>{{ 'testRunNew.sheets.chooseTarget' | translate }}</mat-label>
                <mat-select
                  [value]="assignment.target"
                  [disabled]="isEdit"
                  (valueChange)="setAssignmentTarget(i, $event)"
                  data-testid="tln-assignment-target"
                >
                  @for (option of targetOptions(); track option.id) {
                    <mat-option [value]="option.id">
                      {{ option.id === targetAll ? ('testRunNew.sheets.wholeRun' | translate) : option.label }}
                    </mat-option>
                  }
                </mat-select>
              </mat-form-field>
              <button
                type="button"
                class="tln-icon-btn tln-icon-btn--danger tln-icon-btn--roomy"
                data-testid="tln-remove-assignment"
                [disabled]="isEdit"
                [attr.aria-label]="'testRunNew.sheets.remove' | translate"
                (click)="removeAssignment(i)"
              >
                <mat-icon aria-hidden="true">close</mat-icon>
              </button>
              @if (wasRetargeted(i)) {
                <span class="tln-assignment__moved" data-testid="tln-assignment-retargeted" role="status">
                  <mat-icon aria-hidden="true">info</mat-icon>
                  {{ 'testRunNew.sheets.retargeted' | translate }}
                </span>
              }
            </li>
          }
        </ul>

        <div class="tln-assignment-add">
          <mat-form-field appearance="fill" class="tln-select">
            <mat-label>{{ 'testRunNew.sheets.choose' | translate }}</mat-label>
            <mat-select
              [value]="newSheetId()"
              [disabled]="isEdit"
              (valueChange)="newSheetId.set($event)"
              data-testid="tln-new-sheet"
            >
              @for (sheet of sheets(); track sheet.id) {
                <mat-option [value]="sheet.id">{{ sheet.key }} · {{ sheet.name }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          <mat-form-field appearance="fill" class="tln-select">
            <mat-label>{{ 'testRunNew.sheets.chooseTarget' | translate }}</mat-label>
            <mat-select
              [value]="newSheetTarget()"
              [disabled]="isEdit"
              (valueChange)="newSheetTarget.set($event)"
              data-testid="tln-new-target"
            >
              @for (option of targetOptions(); track option.id) {
                <mat-option [value]="option.id">
                  {{ option.id === targetAll ? ('testRunNew.sheets.wholeRun' | translate) : option.label }}
                </mat-option>
              }
            </mat-select>
          </mat-form-field>
          <button
            type="button"
            class="tln-btn tln-btn--primary"
            data-testid="tln-add-assignment"
            [disabled]="isEdit || !canAddAssignment()"
            (click)="addAssignment()"
          >
            <mat-icon aria-hidden="true">add</mat-icon>
            {{ 'testRunNew.sheets.add' | translate }}
          </button>
        </div>
      </section>

      <!-- Details & Historie -->
      <section class="tln-card" aria-labelledby="tln-history-heading">
        <to-section-title
          headingId="tln-history-heading"
          [title]="'testRunNew.history.title' | translate"
          [subtitle]="'testRunNew.history.subtitle' | translate"
        ></to-section-title>

        <div class="tln-history">
          <div>
            <p class="tln-history__label">{{ 'testRunNew.history.created' | translate }}</p>
            <div class="tln-history__person" data-testid="tln-created-by">
              @if (isEdit) {
                <app-avatar [name]="settings()?.createdByName ?? ''"></app-avatar>
                <div>
                  <p class="tln-history__name">{{ settings()?.createdByName ?? '—' }}</p>
                  <p class="tln-history__when">{{ settings()?.createdAt | date: 'dd.MM.yyyy, HH:mm' }}</p>
                </div>
              } @else {
                <app-avatar [name]="currentUserName()"></app-avatar>
                <div>
                  <p class="tln-history__name">{{ currentUserName() }}</p>
                  <p class="tln-history__when">{{ 'testRunNew.history.onSave' | translate }}</p>
                </div>
              }
            </div>
          </div>
          <div>
            <p class="tln-history__label">{{ 'testRunNew.history.updated' | translate }}</p>
            <!-- Das Modell kennt keine bearbeitende Person, nur den Zeitpunkt (NEX-1100). -->
            <div data-testid="tln-updated-by">
              <p class="tln-history__name">—</p>
              @if (isEdit) {
                <p class="tln-history__when">{{ settings()?.updatedAt | date: 'dd.MM.yyyy, HH:mm' }}</p>
              }
            </div>
          </div>
        </div>

        <mat-form-field appearance="fill" floatLabel="always" class="tln-field tln-field--uppercase-label">
          <mat-label>{{ 'testRunNew.history.note' | translate }}</mat-label>
          <textarea
            matInput
            rows="3"
            data-testid="tln-note"
            formControlName="note"
            [placeholder]="'testRunNew.history.notePlaceholder' | translate"
            [attr.maxlength]="fieldLengths.FREETEXT_MAX"
          ></textarea>
          <mat-hint align="end" aria-live="polite">
            {{ form.controls.note.value.length }} / {{ fieldLengths.FREETEXT_MAX }}
          </mat-hint>
        </mat-form-field>
      </section>
    </div>

    <!-- Sidebar -->
    <aside class="tln-side">
      <section class="tln-card" aria-labelledby="tln-props-heading">
        <h2 class="tln-side__heading" id="tln-props-heading">{{ 'testRunNew.properties.title' | translate }}</h2>

        @if (isEdit) {
          <mat-form-field appearance="fill" floatLabel="always" class="tln-field tln-field--uppercase-label">
            <mat-label>{{ 'testRunNew.properties.name' | translate }}</mat-label>
            <input
              matInput
              data-testid="tln-name"
              formControlName="name"
              required
              [attr.maxlength]="fieldLengths.NAME_MAX"
            />
          </mat-form-field>
        }

        <p class="tln-side__label" id="tln-art-label">{{ 'testRunNew.properties.art' | translate }}</p>
        <div class="tln-arts" role="group" aria-labelledby="tln-art-label">
          @for (kind of artOptions; track kind) {
            <button
              type="button"
              class="tln-art"
              data-testid="tln-art"
              [attr.data-art]="kind"
              [class.tln-art--on]="art() === kind"
              [attr.aria-pressed]="art() === kind"
              [disabled]="isEdit"
              (click)="setArt(kind)"
            >
              <mat-icon aria-hidden="true">{{ kind === 'MANUAL' ? 'manage_accounts' : 'settings' }}</mat-icon>
              {{ 'testRun.art.' + kind | translate }}
            </button>
          }
        </div>

        @if (showTarget()) {
          <div class="tln-target" data-testid="tln-target">
            <p class="tln-side__label">{{ 'testRunNew.target.label' | translate }}</p>
            <app-runner-target-select (targetChange)="onTargetChange($event)" />
            @if (submitHintKey(); as hint) {
              <p class="tln-target__hint" data-testid="tln-submit-hint">{{ hint | translate }}</p>
            }
          </div>
        }

        @if (!isEdit && releasesEmpty()) {
          <p class="tln-release-empty" data-testid="release-empty-hint">
            {{ 'testRunNew.properties.releaseEmpty' | translate }}
          </p>
        } @else {
          <mat-form-field appearance="fill" floatLabel="always" class="tln-field tln-field--uppercase-label">
            <mat-label>{{ 'testRunNew.properties.release' | translate }}</mat-label>
            <mat-select
              formControlName="releaseId"
              data-testid="release-select"
              required
              (selectionChange)="onReleaseSelectedByUser()"
              [placeholder]="'testRunNew.properties.releasePlaceholder' | translate"
            >
              @for (release of releases(); track release.id) {
                <mat-option [value]="release.id">{{ release.id }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        }

        <mat-form-field appearance="fill" floatLabel="always" class="tln-field tln-field--uppercase-label">
          <mat-label>{{ 'testRunNew.properties.environment' | translate }}</mat-label>
          <mat-select formControlName="environment" data-testid="tln-environment">
            @for (env of environmentOptions(); track env) {
              <mat-option [value]="env">
                {{ isListedEnvironment(env) ? ('testRun.environment.' + env | translate) : env }}
              </mat-option>
            }
          </mat-select>
        </mat-form-field>

        <app-person-select
          class="tln-field"
          formControlName="owner"
          [label]="'testRunNew.properties.owner' | translate"
          [options]="personOptions()"
          [required]="ownerRequired()"
          [allowNone]="false"
        ></app-person-select>

        <mat-form-field appearance="fill" floatLabel="always" class="tln-field tln-field--uppercase-label">
          <mat-label>{{ 'testRunNew.properties.tags' | translate }}</mat-label>
          <input
            matInput
            data-testid="tln-tags"
            formControlName="tags"
            [placeholder]="'testRunNew.properties.tagsPlaceholder' | translate"
            [attr.maxlength]="fieldLengths.NAME_MAX"
          />
        </mat-form-field>
      </section>

      <section class="tln-card" aria-labelledby="tln-summary-heading">
        <h2 class="tln-side__heading" id="tln-summary-heading">{{ 'testRunNew.summary.title' | translate }}</h2>
        <dl class="tln-summary">
          <dt>{{ 'testRunNew.summary.source' | translate }}</dt>
          <dd data-testid="tln-summary-source">
            @if (summaryPipelineName(); as pipelineName) {
              {{ 'testRunNew.source.REPO' | translate }} · {{ pipelineName }}
            } @else if (activeSourceTile(); as tile) {
              {{ 'testRunNew.source.' + tile | translate }}
            } @else {
              —
            }
          </dd>
          <dt>{{ 'testRunNew.summary.runs' | translate }}</dt>
          <dd data-testid="tln-summary-runs">{{ runCount() || '—' }}</dd>
          <dt>{{ 'testRunNew.summary.release' | translate }}</dt>
          <dd data-testid="tln-summary-release">{{ summaryReleaseLabel() ?? '—' }}</dd>
          <dt>{{ 'testRunNew.summary.environment' | translate }}</dt>
          <dd data-testid="tln-summary-environment">
            <app-umgebung-pill [umgebung]="form.controls.environment.value"></app-umgebung-pill>
          </dd>
          <dt>{{ 'testRunNew.summary.sheets' | translate }}</dt>
          <dd data-testid="tln-summary-sheets">{{ assignments().length }}</dd>
        </dl>
      </section>
    </aside>
  </div>
</div>

<lib-modal
  [title]="'common.unsavedChanges.title' | translate"
  [text]="(isEdit ? 'testRunNew.edit.confirmLeave' : 'testRunNew.confirmLeave') | translate"
  [visible]="showDeactivateConfirm()"
  [saveText]="'common.unsavedChanges.leave' | translate"
  [cancelText]="'common.unsavedChanges.stay' | translate"
  (save)="onDeactivateConfirmed()"
  (cancel)="onDeactivateCancelled()"
  (visibleChange)="!$event && onDeactivateCancelled()"
></lib-modal>
